import React, { useState, useRef, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Dropdown } from 'src/components/Forms/Dropdown';
import { Svg } from 'src/components/Svg/Svg';

import classes from './Selector.scss';

type Props = {
    title?: string;
    accessibleName?: string;
    selectItems: any;
    selectOptions: any;
    handleChangeState: any;
    initialState?: string | number;
    field: string;
    short?: boolean;
    onOpenChange?(open: boolean): void;
};

export const Selector: React.FC<Props> = ({
    title,
    accessibleName,
    selectItems,
    selectOptions,
    handleChangeState,
    initialState,
    field,
    short = false,
    onOpenChange,
}) => {
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const optionsRef = useRef<HTMLDivElement>(null);
    const openSelectModal = () => setModalIsOpen(true);
    const closeSelectModal = () => setModalIsOpen(false);

    const handleCloseModalState = () => {
        setTimeout(() => {
            closeSelectModal();
        }, 100);
    };

    useEffect(() => {
        if (!modalIsOpen) return undefined;

        document.body.classList.add('selector-modal-open');

        return () => {
            document.body.classList.remove('selector-modal-open');
        };
    }, [modalIsOpen]);

    useEffect(() => {
        if (modalIsOpen && optionsRef.current) {
            const optionsElement = optionsRef.current;

            let startY = 0;
            let startScrollTop = 0;
            let isScrolling = false;
            const SCROLL_THRESHOLD = 10;

            const handleTouchStart = (e: TouchEvent) => {
                startY = e.touches[0].clientY;
                startScrollTop = optionsElement.scrollTop;
                isScrolling = false;
            };

            const handleTouchMove = (e: TouchEvent) => {
                const deltaY = startY - e.touches[0].clientY;

                if (!isScrolling && Math.abs(deltaY) > SCROLL_THRESHOLD) {
                    isScrolling = true;
                }

                if (isScrolling) {
                    optionsElement.scrollTop = startScrollTop + deltaY;
                    e.preventDefault();
                }
            };

            const handleTouchEnd = () => {
                isScrolling = false;
            };

            optionsElement.addEventListener('touchstart', handleTouchStart, { passive: true });
            optionsElement.addEventListener('touchmove', handleTouchMove, { passive: false });
            optionsElement.addEventListener('touchend', handleTouchEnd);

            return () => {
                optionsElement.removeEventListener('touchstart', handleTouchStart);
                optionsElement.removeEventListener('touchmove', handleTouchMove);
                optionsElement.removeEventListener('touchend', handleTouchEnd);
            };
        }
    }, [modalIsOpen]);

    const pickerName = accessibleName || title || 'Select option';

    return (
        <div className={classes.productSelect}>
            {title && <span className={classes.label}>{title}</span>}
            <div className={classes.dropdown}>
                <button
                    type="button"
                    className={classes.dropdownOverlay}
                    onClick={openSelectModal}
                    aria-label={pickerName}
                />
                <Dropdown
                    options={selectOptions}
                    label=""
                    field={field}
                    onChange={(value) => handleChangeState(value)}
                    currentValue={initialState}
                    short={short}
                    onOpenChange={onOpenChange}
                />
            </div>
            <Dialog.Root open={modalIsOpen} onOpenChange={(open) => !open && closeSelectModal()}>
                <Dialog.Portal>
                    <Dialog.Overlay className={classes.selectorOverlay}>
                        <Dialog.Content
                            className={classes.modalContainer}
                            aria-label={pickerName}
                            aria-describedby={undefined}
                        >
                            <div className={classes.modal}>
                                <button className={classes.backButton} type="button" onClick={closeSelectModal}>
                                    <Svg name="arrow-left" />
                                    <span>Back</span>
                                </button>
                                <p className={classes.title}>{title}</p>
                                <div className={classes.optionsContainer}>
                                    <div ref={optionsRef} onClick={handleCloseModalState} className={classes.options}>
                                        {selectItems}
                                    </div>
                                </div>
                            </div>
                        </Dialog.Content>
                    </Dialog.Overlay>
                </Dialog.Portal>
            </Dialog.Root>
        </div>
    );
};
